/**
 * Modal de edição de broadcasts pending da Esteira.
 *
 * Usado pela aba "Próximos envios" — clica "Editar" em um grupo de
 * (horário + template) e abre esse modal.
 *
 * Permite:
 *   - Editar título, subtítulo, image_url e botões
 *   - Escolher escopo: aplicar em todas as páginas do template+horário OU
 *     só na página específica selecionada
 *   - Preview live (PreviewBubble-like inline)
 *
 * Backend: PATCH /api/v1/esteira/broadcasts/batch-edit
 *   { broadcast_ids: [], title?, subtitle?, image_url?, buttons? }
 *
 * Só pending — o backend rejeita IDs sent/sending/etc (skipped).
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import {
    batchEditEsteiraBroadcasts,
    type EsteiraBroadcastBatchEditPayload,
} from '@/services/api';

type BroadcastItem = {
    id: number;
    page_id: number;
    /** Nome da página vindo do backend (resolvido sem filtrar is_active). */
    page_name?: string | null;
    /** Page id real do Facebook. */
    fb_page_id?: string | null;
    scheduled_at: string;
    template_intel_id: number;
    message_payload?: any;
    applied_buttons?: any;
};

type ButtonForm = { title: string; url: string };

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Todos os broadcasts do template+horário (1 ou N). */
    items: BroadcastItem[];
    /** Map page_id → nome legível pra exibir no select. */
    pageNameById: Map<number, string>;
    /** ID do grupo, pra invalidar a query da página. */
    groupId: number;
    /** Label do horário pro header (ex: "27/05/26, 18:00"). */
    horarioLabel: string;
    /** Template ID pro header. */
    tplId: number;
}

const MAX_BUTTONS = 3;

export default function EsteiraBroadcastEditDialog({
    open, onOpenChange, items, pageNameById, groupId, horarioLabel, tplId,
}: Props) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    // Item "sample" pra extrair valores iniciais do template (todos os items
    // têm o mesmo conteúdo já que estão no mesmo grupo template+horário).
    const sample = items[0];

    // Escopo: 'all' = todos os broadcasts; ou um broadcast.id específico (number).
    const [scope, setScope] = useState<'all' | number>('all');

    // Estado do form
    const [title, setTitle] = useState('');
    const [subtitle, setSubtitle] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [buttons, setButtons] = useState<ButtonForm[]>([]);
    // Detecta se é card generic (com elements + image) ou button template
    const [isGenericCard, setIsGenericCard] = useState(true);

    // Inicializa form com valores atuais quando abre
    useEffect(() => {
        if (!open || !sample) return;
        const { title: t, subtitle: s, imageUrl: img, buttons: btns, isCard } = extractFromPayload(sample);
        setTitle(t || '');
        setSubtitle(s || '');
        setImageUrl(img || '');
        setButtons(btns.map((b) => ({ title: b.title || '', url: b.url || '' })));
        setIsGenericCard(isCard);
        setScope('all');
    }, [open, sample]);

    const broadcastIdsToUpdate = useMemo<number[]>(() => {
        if (scope === 'all') return items.map((i) => i.id);
        return [scope];
    }, [scope, items]);

    const mutation = useMutation({
        mutationFn: async () => {
            const payload: EsteiraBroadcastBatchEditPayload = {
                broadcast_ids: broadcastIdsToUpdate,
                title: title || undefined,
                subtitle: isGenericCard ? (subtitle || undefined) : undefined,
                image_url: isGenericCard ? (imageUrl || undefined) : undefined,
                buttons: buttons.length > 0
                    ? buttons.map((b) => ({ title: b.title, url: b.url }))
                    : undefined,
            };
            return batchEditEsteiraBroadcasts(payload);
        },
        onSuccess: (r) => {
            toast({
                title: 'Envio atualizado',
                description: `${r.updated} ${r.updated === 1 ? 'envio editado' : 'envios editados'}`
                    + (r.skipped > 0 ? ` · ${r.skipped} ignorados (já enviados)` : '') + '.',
            });
            queryClient.invalidateQueries({ queryKey: ['esteira-upcoming', groupId] });
            onOpenChange(false);
        },
        onError: (e: any) => {
            toast({
                title: 'Falha ao salvar',
                description: e?.response?.data?.detail || e?.message || 'erro desconhecido',
                variant: 'destructive',
            });
        },
    });

    const addButton = () => {
        if (buttons.length >= MAX_BUTTONS) return;
        setButtons((prev) => [...prev, { title: '', url: '' }]);
    };
    const updateButton = (i: number, field: keyof ButtonForm, v: string) => {
        setButtons((prev) => prev.map((b, idx) => (idx === i ? { ...b, [field]: v } : b)));
    };
    const removeButton = (i: number) => {
        setButtons((prev) => prev.filter((_, idx) => idx !== i));
    };

    // Validações simples — pelo menos título não pode estar vazio.
    const isValid = title.trim().length > 0
        && buttons.every((b) => b.title.trim().length > 0 && b.url.trim().length > 0);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Editar envio</DialogTitle>
                    <DialogDescription>
                        {horarioLabel} · tpl #{tplId} · {items.length} {items.length === 1 ? 'página' : 'páginas'}
                    </DialogDescription>
                </DialogHeader>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* ── Form ── */}
                    <div className="space-y-4">
                        {/* Escopo */}
                        <div className="space-y-1.5">
                            <Label htmlFor="scope">Aplicar em</Label>
                            <select
                                id="scope"
                                value={scope}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setScope(v === 'all' ? 'all' : Number(v));
                                }}
                                className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                            >
                                <option value="all">
                                    Todas as páginas ({items.length})
                                </option>
                                {items.map((it) => {
                                    const label = it.page_name
                                        || pageNameById.get(it.page_id)
                                        || (it.fb_page_id ? `FB: ${it.fb_page_id}` : `Página #${it.page_id}`);
                                    return (
                                        <option key={it.id} value={it.id}>
                                            Só esta: {label}
                                        </option>
                                    );
                                })}
                            </select>
                            <p className="text-[11px] text-gray-500">
                                {scope === 'all'
                                    ? 'A edição será aplicada nos envios pendentes de todas as páginas listadas.'
                                    : 'A edição será aplicada apenas no envio dessa página específica.'}
                            </p>
                        </div>

                        {/* Título */}
                        <div className="space-y-1.5">
                            <Label htmlFor="b-title">Título</Label>
                            <Input
                                id="b-title"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                maxLength={80}
                                placeholder="Ex: FIAT UNO MILLE FIRE"
                            />
                        </div>

                        {/* Subtítulo (só pra generic) */}
                        {isGenericCard && (
                            <div className="space-y-1.5">
                                <Label htmlFor="b-subtitle">Descrição</Label>
                                <Textarea
                                    id="b-subtitle"
                                    value={subtitle}
                                    onChange={(e) => setSubtitle(e.target.value)}
                                    maxLength={80}
                                    rows={2}
                                    placeholder="Texto secundário do card"
                                />
                                <p className="text-[11px] text-gray-500 text-right">
                                    {subtitle.length}/80
                                </p>
                            </div>
                        )}

                        {/* Imagem (só pra generic) */}
                        {isGenericCard && (
                            <div className="space-y-1.5">
                                <Label htmlFor="b-image">URL da imagem</Label>
                                <Input
                                    id="b-image"
                                    value={imageUrl}
                                    onChange={(e) => setImageUrl(e.target.value)}
                                    placeholder="https://..."
                                />
                            </div>
                        )}

                        {/* Botões */}
                        <div className="space-y-1.5">
                            <div className="flex items-center justify-between">
                                <Label>Botões ({buttons.length}/{MAX_BUTTONS})</Label>
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={addButton}
                                    disabled={buttons.length >= MAX_BUTTONS}
                                >
                                    <Plus className="w-3.5 h-3.5 mr-1" /> Adicionar
                                </Button>
                            </div>
                            {buttons.length === 0 && (
                                <p className="text-[11px] text-gray-400 italic">
                                    Sem botões — clique em "Adicionar" pra incluir.
                                </p>
                            )}
                            <div className="space-y-2">
                                {buttons.map((b, i) => (
                                    <div key={i} className="border rounded-md p-2 space-y-2 bg-gray-50">
                                        <div className="flex items-center justify-between">
                                            <span className="text-[10px] uppercase text-gray-500 font-medium tracking-wide">
                                                Botão {i + 1}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => removeButton(i)}
                                                className="text-gray-400 hover:text-red-600"
                                                title="Remover"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                        <Input
                                            placeholder="Texto do botão (ex: VER OS CARROS)"
                                            value={b.title}
                                            maxLength={20}
                                            onChange={(e) => updateButton(i, 'title', e.target.value)}
                                        />
                                        <Input
                                            placeholder="https://destino.com/..."
                                            value={b.url}
                                            onChange={(e) => updateButton(i, 'url', e.target.value)}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* ── Preview live ── */}
                    <div className="space-y-2">
                        <Label>Preview</Label>
                        <div className="bg-gray-50 rounded-2xl rounded-tl-sm border border-gray-200 overflow-hidden max-w-[300px]">
                            {isGenericCard && imageUrl && (
                                <div className="aspect-[1.91/1] bg-gray-100 overflow-hidden">
                                    <img
                                        src={imageUrl}
                                        alt=""
                                        loading="lazy"
                                        className="w-full h-full object-cover"
                                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                    />
                                </div>
                            )}
                            <div className="p-2.5 space-y-0.5">
                                {title && (
                                    <p className="text-[13px] font-semibold text-gray-900 line-clamp-2 leading-tight">
                                        {title}
                                    </p>
                                )}
                                {isGenericCard && subtitle && (
                                    <p className="text-[11px] text-gray-600 line-clamp-2 leading-tight">
                                        {subtitle}
                                    </p>
                                )}
                            </div>
                            {buttons.length > 0 && (
                                <div className="border-t bg-white">
                                    {buttons.map((b, i) => (
                                        <div
                                            key={i}
                                            className={`px-3 py-1.5 text-center text-[11px] text-violet-600 font-medium truncate ${i > 0 ? 'border-t border-gray-100' : ''}`}
                                        >
                                            {b.title || '(sem texto)'}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <p className="text-[10px] text-gray-400">
                            Preview aproximado — o Messenger pode renderizar de forma ligeiramente diferente.
                        </p>
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={mutation.isPending}
                    >
                        Cancelar
                    </Button>
                    <Button
                        type="button"
                        onClick={() => mutation.mutate()}
                        disabled={!isValid || mutation.isPending}
                    >
                        {mutation.isPending && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
                        Salvar {scope === 'all' ? `(${items.length})` : ''}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ─── Extrai valores iniciais do message_payload pra popular o form ───

function extractFromPayload(broadcast: BroadcastItem): {
    title: string;
    subtitle: string;
    imageUrl: string;
    buttons: Array<{ title: string; url: string }>;
    isCard: boolean;
} {
    const payload = broadcast.message_payload || {};
    const att = payload.attachment || {};
    const attPayload = att.payload || {};
    const isCard = attPayload.template_type === 'generic' || Array.isArray(attPayload.elements);

    let title = '';
    let subtitle = '';
    let imageUrl = '';
    let buttons: Array<{ title: string; url: string }> = [];

    if (isCard && Array.isArray(attPayload.elements) && attPayload.elements[0]) {
        const el = attPayload.elements[0];
        title = el.title || '';
        subtitle = el.subtitle || '';
        imageUrl = el.image_url || '';
        buttons = (el.buttons || []).map((b: any) => ({
            title: b.title || '',
            url: b.url || '',
        }));
    } else {
        title = attPayload.text || '';
        buttons = (attPayload.buttons || []).map((b: any) => ({
            title: b.title || '',
            url: b.url || '',
        }));
    }

    // Fallback pros buttons via applied_buttons (caso preview venha sem)
    if (buttons.length === 0 && Array.isArray(broadcast.applied_buttons)) {
        buttons = broadcast.applied_buttons.map((b: any) => ({
            title: b.title || '',
            url: b.url || '',
        }));
    }

    return { title, subtitle, imageUrl, buttons, isCard };
}
